import { useState } from "react";
import { toast } from "sonner";
import { Lightbulb, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Empty, Loader, Pill } from "@/components/Bits";

const TIERS = ["basic", "intermediate", "advanced", "expert"];

export const PracticePanel = ({ scope, id, label }) => {
  const [tier, setTier] = useState("basic");
  const [problems, setProblems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});

  const generate = async () => {
    setBusy(true);
    setProblems([]);
    setOpen({});
    try {
      const { data } =
        scope === "chapter"
          ? await api.post(`/chapters/${id}/practice/generate`, { tier, count: 5 })
          : await api.post("/practice/generate", { topic_id: id, tier, count: 5 });
      toast.info("Practice agent is building your set…");
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data: set } = await api.get(`/practice/${data.set_id}`);
          if (set.status === "ready") {
            clearInterval(poll);
            setProblems(set.problems);
            setBusy(false);
            toast.success(`${set.problems.length} ${set.tier} problems ready`);
          } else if (set.status === "failed" || Date.now() - started > 240000) {
            clearInterval(poll);
            setBusy(false);
            toast.error("The practice agent could not finish — try again");
          }
        } catch {
          clearInterval(poll);
          setBusy(false);
        }
      }, 5000);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid={`practice-panel-${scope}`}>
      <div className="card-flat p-6 flex flex-wrap items-end gap-4">
        <div>
          <p className="rule-label">Difficulty tier</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {TIERS.map((t) => (
              <button
                key={t}
                data-testid={`practice-tier-${t}`}
                onClick={() => setTier(t)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  tier === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:border-slate-500"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="practice-generate" onClick={generate} disabled={busy} className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
          <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : problems.length ? "Generate 5 more" : "Generate problems"}
        </button>
      </div>

      {label && <p className="mt-3 text-xs text-slate-500">{label}</p>}
      {busy && <Loader label="Building your set…" />}

      <div className="mt-6 space-y-4" data-testid="practice-deck">
        {problems.map((p, i) => (
          <div key={p.problem_id} className="card-flat p-6" data-testid={`practice-problem-${i}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono-x text-xs text-slate-400">P{i + 1}</span>
              <Pill tone="amber">{p.tier}</Pill>
              <Pill tone="slate">{p.type}</Pill>
              {p.topic && <Pill tone="blue">{p.topic}</Pill>}
            </div>
            <p className="mt-3 font-semibold">{p.question}</p>
            {p.options?.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-slate-600">
                {p.options.map((o) => (
                  <li key={o} className="pl-3 border-l-2 border-slate-200">
                    {o}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex gap-2">
              <button data-testid={`practice-hint-${i}`} onClick={() => setOpen({ ...open, [`h${i}`]: !open[`h${i}`] })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-xs font-semibold hover:border-amber-500 transition-colors">
                <Lightbulb className="w-3.5 h-3.5 text-amber-600" /> Hint
              </button>
              <button data-testid={`practice-solution-${i}`} onClick={() => setOpen({ ...open, [`s${i}`]: !open[`s${i}`] })} className="px-4 py-2 rounded-full border border-slate-300 text-xs font-semibold hover:border-slate-500 transition-colors">
                Solution
              </button>
            </div>
            {open[`h${i}`] && <p className="mt-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-4">{p.hint}</p>}
            {open[`s${i}`] && (
              <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-4 whitespace-pre-wrap">
                <p className="font-semibold mb-1">Answer: {p.answer}</p>
                {p.solution}
              </div>
            )}
          </div>
        ))}
        {!problems.length && !busy && <Empty title="No problems yet" note="Pick a tier and generate a batch. Repeat as often as you like — it never runs out." />}
      </div>
    </div>
  );
};
